import React, {useEffect, useState} from 'react'
import {CircularProgress, Link, Stack, Typography} from "@mui/material"
import {Link as RouterLink} from "react-router-dom"
import NumberInput from "../form/NumberInput"
import AsyncButton from "../button/AsyncButton"
import ModelFinder from "../models/ModelFinder"
import TemplateSelect from "../templates/TemplateSelect"
import ErrorMessage from "../error/ErrorMessage"
import {ProjectJson} from "../../api/json/ProjectJson"
import SynthesisSettingsJson from "../../api/json/SynthesisSettingsJson"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import {AnyModelSpecJson} from "../../api/json/ModelSpecJson"
import {SynthesisRequests} from "../../api/requests/SynthesisRequests"
import {useModelSpecs} from "../../hooks/useModelSpecs"
import {useIntegrations} from "../../hooks/useIntegrations"
import {useToasts} from "../../hooks/useToasts"
import ApiError from "../../api/ApiError"

interface SynthesisSettingsTabProps {
    project: ProjectJson
}

function SynthesisSettingsTab({project}: SynthesisSettingsTabProps) {
    const {openAi} = useIntegrations()
    const {models} = useModelSpecs()
    const toasts = useToasts()

    const [settings, setSettings] = useState<SynthesisSettingsJson>()
    const [loadError, setLoadError] = useState<ApiError>()

    const [model, setModel] = useState<AnyModelSpecJson | undefined>()
    const [template, setTemplate] = useState<AnyTemplateJson | undefined>()
    const [batchSize, setBatchSize] = useState<number>(1)
    const [validationRatio, setValidationRatio] = useState<number>(0)

    const [saving, setSaving] = useState(false)

    useEffect(() => {
        SynthesisRequests.getSettings(project.id)
            .then(s => {
                setSettings(s)
                setBatchSize(s.batchSize)
                setValidationRatio(s.validationRatio)
                const t = project.templates.find(it => it.id === s.templateId)
                if (t) setTemplate(t)
            })
            .catch(setLoadError)
    }, [project.id])

    // Sync model spec once both the model list and the loaded settings are available.
    useEffect(() => {
        if (!models || !settings) return
        setModel(models.find(it => it.name === settings.chatModel))
    }, [models, settings])

    if (!openAi) {
        return (
            <Typography variant="body2">
                Set up your <Link component={RouterLink} to="/integrations/open-ai">OpenAI integration</Link> to
                configure synthesis.
            </Typography>
        )
    }

    if (loadError) {
        return <ErrorMessage error={loadError}/>
    }

    if (!settings) {
        return <CircularProgress/>
    }

    const unsavedChanges = (
        model?.name !== settings.chatModel
        || template?.id !== settings.templateId
        || batchSize !== settings.batchSize
        || validationRatio !== settings.validationRatio
    )

    const handleSave = () => {
        if (!model || !template) return
        setSaving(true)
        SynthesisRequests.updateSettings(project.id, {
            ...settings,
            chatModel: model.name,
            templateId: template.id,
            batchSize: batchSize,
            validationRatio: validationRatio,
        })
            .then(updated => {
                setSettings(updated)
                toasts.showSaved()
            })
            .catch(toasts.showError)
            .finally(() => setSaving(false))
    }

    return (
        <Stack spacing={3} sx={{maxWidth: 520}}>
            <ModelFinder instructModelsOnly value={model} onChange={setModel}/>

            <TemplateSelect templates={project.templates} value={template} onChange={setTemplate}/>

            <NumberInput label="Batch size"
                         helperText="How many rows to synthesize per API call. Higher numbers take longer per request but can save tokens."
                         value={batchSize}
                         min={1}
                         max={10}
                         onBlur={setBatchSize}/>

            <NumberInput label="Validation rows"
                         helperText="Percentage of newly synthesized rows saved to the validation dataset."
                         value={Math.round(validationRatio * 100)}
                         min={0}
                         max={100}
                         onBlur={(newValue) => setValidationRatio(Math.round(newValue) / 100)}
                         endAdornment={<>%</>}/>

            <AsyncButton waiting={saving}
                         disabled={!unsavedChanges || !model || !template}
                         onClick={handleSave}
                         sx={{alignSelf: 'flex-start'}}>
                Save
            </AsyncButton>
        </Stack>
    )
}

export default SynthesisSettingsTab
